/**
 * Augmented Steam — Steam store / community enhancer
 *
 * Declares one web-accessible-resource entry covering `img/*`, `css/*`, `html/*`,
 * `scriptlets/*`, `localization/*`, `changelog.json` and sourcemaps — but scopes its
 * `matches` to Steam only (steampowered.com and steamcommunity.com). Those resources are
 * genuinely unreachable from any other origin, so only Steam can detect the extension;
 * a negative result elsewhere proves nothing.
 *
 * That makes this a scoped detector: we set `requiredOrigin` to the community site and
 * fetch concrete vendor-named files (the glob entries are resolved to real paths on disk).
 *
 * Manifest verified against version 4.7.0.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'dnhpnfgdlenaccegplpojghhmaamnnfp';

export const augmentedSteam: ExtensionDefinition = {
  id: ID,
  name: 'Augmented Steam',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  requiredOrigin: 'https://steamcommunity.com',
  notes:
    'All WAR are scoped to Steam origins, so only steamcommunity.com / steampowered.com ' +
    'can detect it. The vendor-named augmentedsteam.css is conclusive from those pages.',
  signals: [
    {
      id: 'war-augmentedsteam-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch css/augmentedsteam.css — the uniquely vendor-named stylesheet, exposed ' +
        'only to Steam origins.',
      weight: 1,
      run: () => probeFetch(ID, 'css/augmentedsteam.css'),
    },
    {
      id: 'war-changelog-json',
      kind: 'web-accessible-resource',
      description:
        'Fetch changelog.json — a named top-level resource, an independent path ' +
        'corroborating the identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'changelog.json'),
    },
    {
      id: 'war-holidayprofile-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch js/extra/holidayprofile.js — a concrete, hand-listed script path (not a ' +
        'glob), a distinctive third resource.',
      weight: 0.5,
      run: () => probeFetch(ID, 'js/extra/holidayprofile.js'),
    },
  ],
};
